// Marker: payment_link_v1
// Public payment page — accessible without auth via /pay/:token
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SlipUpload from './SlipUpload'; /* payment_slip_v1 */
import { QRCodeSVG } from 'qrcode.react'; /* bank_promptpay_v1 */
import { paymentQrPayload, injectAmount } from '../lib/promptpay'; /* bank_promptpay_v1 + biller_qr_v1 + qr_amount_v1 */

function fmtMoney(n, cur) {
  var s = Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });
  return cur === 'USDT' ? (s + ' USDT') : ('฿' + s);
}

function fmtDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (e) { return d; }
}

function CopyButton(props) {
  const text = props.text;
  const [copied, setCopied] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(String(text || ''));
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 1500);
    } catch (e) {}
  }
  return (
    <button onClick={copy} style={{ background: copied ? '#10b981' : '#f1f5f9', color: copied ? 'white' : '#475569', border: '1px solid ' + (copied ? '#10b981' : '#e2e8f0'), padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {copied ? '✓ คัดลอกแล้ว' : '📋 คัดลอก'}
    </button>
  );
}

export default function PaymentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(function () {
    if (!token) { setError('ลิงก์ไม่ถูกต้อง'); setLoading(false); return; }
    fetch('/api/public/invoices/' + encodeURIComponent(token))
      .then(function (r) {
        if (!r.ok) {
          if (r.status === 404) throw new Error('ไม่พบใบแจ้งหนี้');
          if (r.status === 400) throw new Error('ลิงก์ไม่ถูกต้อง');
          throw new Error('โหลดข้อมูลไม่สำเร็จ');
        }
        return r.json();
      })
      .then(function (d) { setData(d); setLoading(false); })
      .catch(function (e) { setError(e.message || 'เกิดข้อผิดพลาด'); setLoading(false); });
  }, [token]);

  if (loading) {
    return <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ color: '#94a3b8' }}>กำลังโหลด...</div>
    </div>;
  }

  if (error) {
    return <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: 'white', padding: 40, borderRadius: 12, maxWidth: 480, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontSize: 18, color: '#dc2626', marginBottom: 8 }}>{error}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>กรุณาตรวจสอบลิงก์ หรือติดต่อผู้ออกใบแจ้งหนี้</div>
      </div>
    </div>;
  }

  const inv = data;
  const isPaid = inv.status === 'paid';
  const isCancelled = inv.status === 'cancelled';

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          {inv.tenant_name ? <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{inv.tenant_name}</div> : null}
          <div style={{ fontSize: 13, color: '#94a3b8' }}>ใบแจ้งหนี้</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{inv.invoice_number}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
            ออก: {fmtDate(inv.issue_date)}{inv.due_date ? ' • ครบกำหนด: ' + fmtDate(inv.due_date) : ''}
          </div>
          {isPaid ? <div style={{ marginTop: 10, display: 'inline-block', background: '#dcfce7', color: '#15803d', padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>✓ ชำระแล้ว</div> : null}
          {isCancelled ? <div style={{ marginTop: 10, display: 'inline-block', background: '#fee2e2', color: '#b91c1c', padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>ยกเลิก</div> : null}
        </div>

        {/* Items */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>รายละเอียด</div>
          {(inv.items || []).map(function (it, i) {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < inv.items.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 14 }}>
                <div>
                  <div style={{ color: '#334155' }}>{it.description}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>× {Number(it.quantity)} @ {Number(it.unit_price).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                </div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{Number(it.line_total).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              </div>
            );
          })}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '2px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 14, color: '#475569' }}>ยอดรวม</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{fmtMoney(inv.total_amount, inv.currency)}</div>
          </div>
          {/* usdt_emphasis_v1: เน้นยอด USDT ใหญ่+แดง */}
          {inv.total_amount_usdt && Number(inv.total_amount_usdt) > 0 ? (
            <div style={{ textAlign: 'right', fontSize: 20, fontWeight: 700, color: '#dc2626', marginTop: 4 }}>
              ≈ {Number(inv.total_amount_usdt).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT
              {inv.usdt_rate ? <span style={{ color: '#94a3b8' }}> (@ {Number(inv.usdt_rate).toLocaleString('th-TH')})</span> : null}
            </div>
          ) : null}
        </div>

        {/* Payment method section */}
        {!isPaid && !isCancelled ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>💳 ช่องทางการชำระเงิน</div>

            {/* Bank transfer */}
            {(inv.payment_method === 'bank' || inv.payment_method === 'bank_transfer') && inv.bank_account ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>ธนาคาร</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{inv.bank_account.bank_code}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>เลขที่บัญชี</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', flex: 1 }}>{inv.bank_account.account_number}</div>
                  <CopyButton text={inv.bank_account.account_number} />
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>ชื่อบัญชี</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{inv.bank_account.account_name}</div>
                {inv.bank_account.branch ? <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>สาขา: {inv.bank_account.branch}</div> : null}
              </div>
            ) : null}

            {/* PromptPay */}
            {inv.payment_method === 'promptpay' && inv.bank_account && inv.bank_account.promptpay_id ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>{inv.bank_account.promptpay_type === 'biller' ? '🏪 Thai QR / แม่มณี' : '📱 พร้อมเพย์'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', flex: 1 }}>{inv.bank_account.promptpay_id}</div>
                  <CopyButton text={inv.bank_account.promptpay_id} />
                </div>
                {inv.bank_account.account_name ? <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>ชื่อ: {inv.bank_account.account_name}</div> : null}
                {/* bank_promptpay_v1: QR พร้อมเพย์ (ใส่ยอดเงิน) */}
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'white', borderRadius: 10, padding: 14 }}>
                  <div style={{ background: 'white', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <QRCodeSVG value={paymentQrPayload(inv.bank_account.promptpay_id, inv.total_amount, inv.bank_account.promptpay_type)} size={200} level="M" includeMargin={false} />
                  </div>
                  <div style={{ fontSize: 13, color: '#0f172a', marginTop: 8, fontWeight: 600 }}>{inv.bank_account.promptpay_type === 'biller' ? 'สแกนแล้วกรอกยอด ' : 'สแกนจ่าย '}{fmtMoney(inv.total_amount, 'THB')}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{inv.bank_account.promptpay_type === 'biller' ? 'เปิดแอปธนาคาร → สแกน QR → กรอกยอดเงินตามด้านบน' : 'เปิดแอปธนาคาร → สแกน QR → ยอดจะขึ้นให้อัตโนมัติ'}</div>
                </div>
              </div>
            ) : null}

            {/* qr_payload_v1: QR จากโค้ดจริง (แม่มณี / Thai QR) — สแกนได้ตรงตัวจริง */}
            {inv.bank_account && inv.bank_account.qr_payload ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>🏪 สแกน QR เพื่อจ่าย</div>
                <div style={{ background: 'white', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <QRCodeSVG value={injectAmount(inv.bank_account.qr_payload, inv.total_amount)} size={220} level="M" includeMargin={false} />
                </div>
                <div style={{ fontSize: 13, color: '#0f172a', marginTop: 8, fontWeight: 600 }}>สแกนจ่าย {fmtMoney(inv.total_amount, 'THB')}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>เปิดแอปธนาคาร → สแกน QR → ยอดจะขึ้นให้อัตโนมัติ</div>
              </div>
            ) : null}

            {/* qr_image_v1: QR ร้านค้าอัปโหลด (แม่มณี / K SHOP ฯลฯ) */}
            {inv.bank_account && inv.bank_account.qr_image_url ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>🖼️ สแกน QR ร้านค้าเพื่อจ่าย</div>
                <img src={inv.bank_account.qr_image_url} alt="QR ร้านค้า" style={{ maxWidth: 240, width: '100%', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white' }} />
                <div style={{ fontSize: 13, color: '#0f172a', marginTop: 8, fontWeight: 600 }}>ยอดที่ต้องชำระ {fmtMoney(inv.total_amount, 'THB')}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>สแกนแล้วกรอกยอดเงินตามด้านบน</div>
              </div>
            ) : null}

            {/* Crypto */}
            {inv.payment_method === 'crypto' || inv.payment_method === 'usdt_trc20' || inv.payment_method === 'usdt_erc20' ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>🪙 Crypto Wallet {inv.crypto_network ? '(' + inv.crypto_network + ')' : ''}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{inv.crypto_wallet_address || (inv.bank_account && (inv.crypto_network === 'TRC20' ? inv.bank_account.crypto_wallet_trc20 : inv.bank_account.crypto_wallet_erc20)) || '-'}</div>
                  <CopyButton text={inv.crypto_wallet_address || (inv.bank_account && (inv.crypto_network === 'TRC20' ? inv.bank_account.crypto_wallet_trc20 : inv.bank_account.crypto_wallet_erc20)) || ''} />
                </div>
                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 8 }}>⚠️ โอนเฉพาะใน network {inv.crypto_network || ''} เท่านั้น</div>
              </div>
            ) : null}

            {/* Cash / unspecified */}
            {!inv.payment_method || inv.payment_method === 'cash' || inv.payment_method === 'other' ? (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, color: '#64748b', fontSize: 13 }}>
                {inv.payment_method === 'cash' ? '💵 เงินสด — ติดต่อผู้ออกใบแจ้งหนี้' : 'ติดต่อผู้ออกใบแจ้งหนี้สำหรับวิธีชำระเงิน'}
              </div>
            ) : null}

            <SlipUpload token={token} currency={inv.currency} />
          </div>
        ) : null}

        {/* Note */}
        {inv.note ? (
          <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 12, fontSize: 13, color: '#475569', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>หมายเหตุ</div>
            {inv.note}
          </div>
        ) : null}

        <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 20, paddingBottom: 20 }}>
          ส่งถึง: {inv.recipient_name}
        </div>
      </div>
    </div>
  );
}
